import { observable, action, computed, makeObservable } from 'mobx';
import sortBy from 'lodash/sortBy';

import { StepNames } from 'shared/helpers';
import { logger, LogEvents } from 'kioskApp/helpers';
import { StoreModel } from 'kioskApp/models/Index';
import { WizardStep, Payment } from 'shared/interfaces';

export class WizardModel {
    @observable
    public currentStep: WizardStep;

    @observable
    public steps: WizardStep[];

    constructor(private root: StoreModel) {
        makeObservable(this);
    }

    @action
    init = () => {
        this.setSteps(this.root.configuration.steps);
        this.currentStep = this.steps[0];
    };

    @action
    goToStep = (stepName: StepNames) => {
        logger.logInformation(
            'Wizard Step changed.',
            LogEvents.WizardStepChanged,
            {
                previousStep:
                    this.currentStep && this.currentStep.name.toString(),
                currentStep: stepName.toString(),
            }
        );

        this.currentStep = this.getStepByName(stepName);
    };

    @action
    goToFirstStep = () => {
        this.currentStep = this.steps[0];
    };

    @action
    goBack = () => {
        const currentIndex = this.steps.indexOf(this.currentStep);
        if (currentIndex > 0) {
            const nextIndex = (currentIndex - 1) % this.steps.length;
            const nextStep = this.steps[nextIndex];

            logger.logInformation(
                'Wizard Step changed.',
                LogEvents.WizardStepChanged,
                {
                    previousStep:
                        this.currentStep && this.currentStep.name.toString(),
                    currentStep: nextStep.name.toString(),
                }
            );

            this.currentStep = nextStep;
        }
    };

    @action
    goNext = () => {
        const currentIndex = this.steps.indexOf(this.currentStep);
        const nextIndex = (currentIndex + 1) % this.steps.length;
        const nextStep = this.steps[nextIndex];

        if (currentIndex >= this.totalSteps - 1) {
            this.root.kioskSdk.goPayment({
                billpayResponse: this.root.billpay.customer,

                appId: this.root.appId,
                sku: this.root.sku,
                description: this.root.description,
                serviceCode: this.root.serviceCode,
                paymentApiUrl: this.root.paymentApiUrl,
            } as Payment);
            return;
        }

        logger.logInformation(
            'Wizard Step changed.',
            LogEvents.WizardStepChanged,
            {
                previousStep:
                    this.currentStep && this.currentStep.name.toString(),
                currentStep: nextStep.name.toString(),
            }
        );

        this.currentStep = nextStep;
    };

    @action
    private setSteps = (steps: WizardStep[]) => {
        this.steps = sortBy(steps, (x) => x.stepNumber);
        this.steps.forEach((step, index) => {
            if (step.stepNumber !== index + 1) step.stepNumber = index + 1;
        });
    };

    @computed
    get totalSteps(): number {
        if (!this.steps) {
            return 0;
        }
        return this.steps.length;
    }

    private getStepByName(name: string) {
        return this.steps.find((x) => x.name === name) || this.steps[0];
    }
}

export default WizardModel;
